import { Action, Module, Mutation } from "vuex-module-decorators";
import store from "@/store";
import { ChallengesApi } from "@/api/challenges.api";
import { ChallengeModel, VisitorsResponse } from "@/models/challenge.model";
import { take } from "rxjs/operators";
import { EntityStateAdapter } from "@/store/base.store";
import { i18nState } from "@/utils/store-acessor";

export const featureKey = "challenges";

// @ts-ignore
if (store.state.challenges) {
  store.unregisterModule(featureKey);
}

@Module({
  store,
  dynamic: true,
  name: featureKey,
  namespaced: true,
  stateFactory: true
})
export class ChallengesModule extends EntityStateAdapter<ChallengeModel> {
  private _selectedChallenge: ChallengeModel | null = null;

  get challengesByTag(): (tag: string) => ChallengeModel[] {
    return (tag: string) =>
      this._entities.filter(v => {
        return v.tags?.filter(t => t.name == tag);
      });
  }

  get challenges(): ChallengeModel[] {
    return this._entities;
  }

  get loading(): boolean {
    return this._loading;
  }

  get currentPage(): number {
    return this._currentPage;
  }

  get lastPage(): number {
    return this._lastPage;
  }

  get selectedChallenge(): ChallengeModel | null {
    return this._selectedChallenge;
  }

  get hasPrivilegeAccess(): boolean {
    return !!(
      this._selectedChallenge?.is_judge ||
      this._selectedChallenge?.is_advisor ||
      this._selectedChallenge?.is_challenge_admin
    );
  }

  get isContestant(): boolean {
    return !!this._selectedChallenge?.is_contestant;
  }

  @Mutation
  selectedChallengeLoaded(challenge: ChallengeModel) {
    this._selectedChallenge = challenge;
    store.commit(`${featureKey}/upsertOne`, challenge);
  }

  @Action({ commit: "upsertMany", rawError: true })
  async fetchAll(data: { page: number; tag?: string }, search?: string): Promise<ChallengeModel[]> {
    this.updateLoading(true);

    const response = await new ChallengesApi()
      .getChallengesbyFilter("", data.page, data.tag)

      .pipe(take(1))
      .toPromise();

    this.context.commit("updateAllPageParams", response);

    if (search) {
      console.log("entrou");
      const results = response.data;
      const pattern = new RegExp(search, "i");
      return results.filter(item => pattern.test(String(item.title)));
    }

    return response.data;
  }

  @Action({ commit: "upsertMany", rawError: true })
  async fetchBySearch(data: { page: number; tag?: string }): Promise<ChallengeModel[]> {
    this.updateLoading(true);

    const response = await new ChallengesApi()
      .getChallengesbyFilter("", data.page, "")

      .pipe(take(1))
      .toPromise();

    this.context.commit("updateAllPageParams", response);

    if (data.tag) {
      const lang = i18nState.locale.value;
      const results = response.data;
      return results.filter(item => {
        // @ts-ignore
        const currentItem = lang === "en" ? item.title.en : item.title.pt;
        // @ts-ignore
        return currentItem.toLowerCase().includes(data.tag.toLowerCase());
      });
    }

    return response.data;
  }

  @Action({ commit: "selectedChallengeLoaded", rawError: true })
  async getChallengeById(id: number): Promise<ChallengeModel> {
    return await new ChallengesApi()
      .findById(id)
      .pipe(take(1))
      .toPromise();
  }

  @Action({ commit: "selectedChallengeLoaded", rawError: true })
  async getChallengeBySlug(data: { slug: string }): Promise<ChallengeModel> {
    this.context.commit("loadingUpdated", true);

    const response = await new ChallengesApi()
      .findBySlug(data.slug)
      .pipe(take(1))
      .toPromise();
    this.context.commit("loadingUpdated", false);
    this.context.commit("upsertOne", response);
    return response;
  }

  @Action({ rawError: true })
  async registerAndGetVisitors(id: number): Promise<VisitorsResponse> {
    await new ChallengesApi().registerVisitor(id).toPromise();
    return await new ChallengesApi()
      .getVisitors(id)
      .pipe(take(1))
      .toPromise();
  }

  @Action({ rawError: true })
  async searchByTag(query: string): Promise<ChallengeModel[]> {
    return await new ChallengesApi()
      .searchByTag(query)
      .pipe(take(1))
      .toPromise();
  }
}
